import { useMemo, useRef, useState } from 'react'
import ChampCard from './ChampCard.jsx'
import boardStyles from './boardControls.module.css'
import { PURSUIT_COLUMNS, pursuitColumn, makeComparator } from '../../../lib/championships.js'
import { SortBar } from './SortControls.jsx'
import { nationalsFirst } from '../recognition.js'
import styles from './pursuit.module.css'

// Cards rendered per column before the column offers the rest. A page, not a cap.
const PAGE = 200

/**
 * Card views have no header row, so the sort keys are offered explicitly — and
 * built from the SPACE, not hardcoded: the date is whatever this space counts
 * down to ("Close date" in the RFP space), the name column is the space's noun,
 * and the dimensions only records actually carry (product category, state) are
 * offered only where they exist.
 */
const sortFieldsFor = (noun, dateLabel, { dated, product, state, employees, industry, hq, levelLabel, hero, orgType, pursuitDate, wonDate, heroLabel, productLabel, hqLabel }) => [
  ...(dated ? [{ key: 'date', label: dateLabel }] : []),
  // THE COMMUNITY HERO. In a police / fire space it is the first thing a rep
  // sorts by — the departments with no hero are the worklist.
  ...(hero ? [{ key: 'heroStatus', label: heroLabel }] : []),
  // HEADCOUNT sorts first in an employee-recognition space: it is the single
  // biggest signal of how large a service-award program would be, and it is a
  // real numeric sort (see `makeComparator`), not a lexical one.
  ...(employees ? [{ key: 'employees', label: 'Total employees' }] : []),
  { key: 'owner', label: 'Representative' },
  { key: 'name', label: noun },
  ...(industry ? [{ key: 'industry', label: 'Industry' }] : []),
  ...(hq ? [{ key: 'hqLocation', label: hqLabel }] : []),
  ...(orgType ? [{ key: 'orgType', label: 'Organization type' }] : []),
  ...(product ? [{ key: 'product', label: productLabel }] : []),
  // The Greek pursuit's own dates — offered only where a pursuit deal exists.
  ...(pursuitDate ? [{ key: 'pursuitDate', label: 'Pursuit date' }] : []),
  ...(wonDate ? [{ key: 'wonDate', label: 'Won date' }] : []),
  ...(state ? [{ key: 'state', label: 'State' }] : []),
  ...(dated ? [{ key: 'sport', label: 'Sport' }] : []),
  { key: 'level', label: levelLabel },
  ...(dated ? [{ key: 'incumbent', label: 'Incumbent' }] : []),
]

/**
 * Kanban by pursuit stage; cards sorted by days_to_event ascending by default.
 *
 * `dated: false` is an employee-recognition space — no event date, so the date
 * sort (and the championship-only sport / incumbent keys) are absent and the
 * default sort falls to the biggest signal the records actually carry.
 */
export default function PursuitBoard({
  items, now, onOpen, noun = 'Championship', dateLabel = 'Event date', dated = true,
  groupNationals = false, rowAction = null,
  /* 2026-10-26 [Jarvis · Claude]: the board can MOVE a card (`onMove`, see
   * PursuitMove.jsx) and run its actions (`actions`: the drawer's own
   * onCreateArt / onQuote / onCampaign / onSubmitWin). */
  onMove = null, actions = null,
}) {
  const [limits, setLimits] = useState(() => ({}))
  /* THE JUMP BAR. At 390px the kanban is one 86vw column wide and every
     record lives in To Pursue, so the other four columns were five swipes
     away and read as empty boards. A chip per column, with its count,
     scrolls straight to it. */
  const kanbanRef = useRef(null)
  const [jumped, setJumped] = useState('')
  const jumpTo = (key) => {
    setJumped(key)
    const el = kanbanRef.current?.querySelector(`[data-col="${CSS.escape(key)}"]`)
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
  }
  const SORT_FIELDS = useMemo(
    () => sortFieldsFor(noun, dateLabel, {
      dated,
      product: items.some((c) => c.product),
      state: items.some((c) => c.sourceType && c.state && c.state !== 'NATIONAL'),
      employees: items.some((c) => c.employees != null),
      industry: items.some((c) => c.industry),
      hq: items.some((c) => c.hqLocation),
      levelLabel: items.find((c) => c.levelLabel)?.levelLabel || 'Level',
      // The hero key is offered for the whole SPACE, not only when somebody has
      // already been nominated — with nothing nominated the sort still groups
      // the (empty) hero column, which is the useful end of it.
      hero: rowAction?.kind === 'hero' || items.some((c) => c.hero),
      heroLabel: rowAction?.heroLabel || 'Community Hero',
      orgType: items.some((c) => c.orgType),
      pursuitDate: items.some((c) => c.pursuitDate),
      wonDate: items.some((c) => c.wonDate),
      productLabel: items.find((c) => c.productLabel)?.productLabel || 'Product',
      hqLabel: items.find((c) => c.hqLabel)?.hqLabel || 'HQ location',
    }),
    [items, noun, dateLabel, dated, rowAction]
  )
  const [sort, setSort] = useState({ field: 'date', dir: 'asc' })
  // The board stays mounted across a space switch, so the carried-over sort key
  // may be one this space doesn't offer (date, in Corporate). Fall back to the
  // space's first key rather than sorting by a field that isn't rendered —
  // and open headcount LARGEST first, which is the useful end of it.
  const active = useMemo(() => (
    SORT_FIELDS.some((f) => f.key === sort.field)
      ? sort
      : { field: SORT_FIELDS[0].key, dir: SORT_FIELDS[0].key === 'employees' ? 'desc' : 'asc' }
  ), [SORT_FIELDS, sort])
  const limitFor = (k) => limits[k] ?? PAGE
  const bump = (k, n) => setLimits((s) => ({ ...s, [k]: n }))

  const cmp = useMemo(() => makeComparator(active.field, active.dir, now), [active, now])
  const columns = useMemo(() => {
    const g = {}
    for (const col of PURSUIT_COLUMNS) g[col.key] = []
    for (const c of items) g[pursuitColumn(c)].push(c)
    for (const k of Object.keys(g)) {
      g[k].sort(cmp)
      // Nationals above their chapters, inside every column, whatever the sort.
      if (groupNationals) g[k] = nationalsFirst(g[k])
    }
    return g
  }, [items, cmp, groupNationals])

  return (
    <>
    <SortBar sort={active} setSort={setSort} fields={SORT_FIELDS} label="Sort cards by" />
    <div className={boardStyles.jumpBar} role="group" aria-label="Go to column">
      {PURSUIT_COLUMNS.map((col) => (
        <button key={col.key} type="button" aria-pressed={jumped === col.key}
          className={`${boardStyles.jump} ${jumped === col.key ? boardStyles.jumpOn : ''}`}
          onClick={() => jumpTo(col.key)}>
          {col.label}<span className={boardStyles.jumpCount}>{columns[col.key].length.toLocaleString()}</span>
        </button>
      ))}
    </div>
    {/* `cc-kanban` is the unhashed hook the phone layer sizes the columns
        through — the rule cannot live in pursuit.module.css without moving
        the champion painter's `_tName_` hash. See src/styles/cc-mobile.css. */}
    <div className={`${styles.kanban} cc-kanban`} ref={kanbanRef}>
      {PURSUIT_COLUMNS.map((col) => {
        const list = columns[col.key]
        const limit = limitFor(col.key)
        const shown = limit >= list.length ? list : list.slice(0, limit)
        return (
          <div key={col.key} className={styles.column} data-col={col.key}>
            <div className={styles.columnHead}>
              <span className={styles.columnTitle}>{col.label}</span>
              <span className={styles.columnCount}>{list.length.toLocaleString()}</span>
            </div>
            <div className={styles.columnBody}>
              {list.length === 0 && (
                <div className={styles.columnEmpty}>
                  Nothing is in {col.label} yet. Tap Move on any card to put it here.
                </div>
              )}
              {shown.map((c) => (
                <ChampCard key={c.id} c={c} now={now} onOpen={onOpen} dated={dated} rowAction={rowAction}
                  board onMove={onMove} actions={actions} />
              ))}
              {list.length > shown.length && (
                <div className={styles.more}>
                  <button className={styles.moreBtn} onClick={() => bump(col.key, limit + PAGE * 5)}>
                    Show {Math.min(PAGE * 5, list.length - shown.length).toLocaleString()} more
                  </button>
                  <button className={styles.moreBtn} onClick={() => bump(col.key, list.length)}>
                    Show all {list.length.toLocaleString()}
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
    </>
  )
}
